import { Tournament, TournamentStatus, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { EmptyState, FiltersModal, PatternBackground, TournamentCard } from '@/src/presentation/components';
import { SortOption } from '@/src/presentation/hooks';
import { FontAwesome5, Ionicons } from '@expo/vector-icons';
import { FlatList, Modal, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './HomeScreen.styles';

export interface HomeScreenProps {
  filteredTournaments: Tournament[];
  loading: boolean;
  loadingMore: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filterStatus: TournamentStatus | 'all';
  setFilterStatus: (value: TournamentStatus | 'all') => void;
  filterType: TournamentType | 'all';
  setFilterType: (value: TournamentType | 'all') => void;
  sortBy: SortOption;
  setSortBy: (value: SortOption) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  filtersVisible: boolean;
  setFiltersVisible: (value: boolean) => void;
  menuVisible: boolean;
  setMenuVisible: (value: boolean) => void;
  handleTournamentPress: (tournamentId: string) => void;
  handleCreateTournament: () => void;
  handleMenuPress: (tournament: Tournament, event: any) => void;
  handleEdit: () => void;
  handleDelete: () => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    overlay: string;
    divider: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
  };
}

export const HomeScreen = ({
  filteredTournaments,
  loading,
  loadingMore,
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  filterType,
  setFilterType,
  sortBy,
  setSortBy,
  hasActiveFilters,
  clearFilters,
  filtersVisible,
  setFiltersVisible,
  menuVisible,
  setMenuVisible,
  handleTournamentPress,
  handleCreateTournament,
  handleMenuPress,
  handleEdit,
  handleDelete,
  onRefresh,
  onLoadMore,
  colors,
}: HomeScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderTournamentItem = ({ item }: { item: Tournament }) => (
    <TournamentCard
      tournament={item}
      onPress={() => handleTournamentPress(item.id)}
      onMenuPress={(e) => handleMenuPress(item, e)}
      colors={colors}
    />
  );

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        {/* Search Bar with filter button */}
        <View style={styles.searchRow}>
          <View style={[styles.searchContainer, { backgroundColor: colors.background, borderColor: colors.border }]}>
            <Ionicons name="search" size={20} color={colors.textTertiary} style={{ marginRight: 8 }} />
            <TextInput
              style={[styles.searchInput, { color: colors.text }]}
              placeholder={t('common.search') + '...'}
              placeholderTextColor={colors.textTertiary}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          <TouchableOpacity
            style={[
              styles.filterIconBtn,
              {
                backgroundColor: hasActiveFilters ? colors.primary : colors.card,
                borderColor: colors.border,
              },
            ]}
            onPress={() => setFiltersVisible(true)}
          >
            <Ionicons name="filter" size={22} color={hasActiveFilters ? colors.primaryText : colors.text} />
            {hasActiveFilters && (
              <View style={[styles.filterBadge, { backgroundColor: colors.error }]}>
                <Text style={styles.filterBadgeText}>
                  {[filterStatus !== 'all', filterType !== 'all', sortBy !== 'date-desc'].filter(Boolean).length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>

        {hasActiveFilters && (
          <TouchableOpacity style={[styles.clearAllFiltersBtn, { borderColor: colors.border }]} onPress={clearFilters}>
            <Text style={[styles.clearAllFiltersBtnText, { color: colors.textSecondary }]}>{t('common.clearFilters')}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity style={[styles.createBtn, { backgroundColor: colors.primary }]} onPress={handleCreateTournament}>
          <Text style={[styles.createBtnText, { color: colors.primaryText }]}>{t('tournaments.createTournament')}</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={filteredTournaments}
        keyExtractor={(item) => item.id}
        renderItem={renderTournamentItem}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="trophy-outline" size={52} color={colors.textTertiary} />}
            title={hasActiveFilters ? t('common.noResultsFilters') : t('tournaments.noTournamentsYet')}
            subtitle={hasActiveFilters ? t('common.tryAdjustingFilters') : undefined}
            action={
              !hasActiveFilters
                ? { label: t('tournaments.createTournament'), onPress: handleCreateTournament }
                : undefined
            }
            colors={colors}
          />
        }
        ListFooterComponent={
          loadingMore ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <Text style={[{ color: colors.textSecondary, fontSize: 14 }]}>{t('common.loading')}</Text>
            </View>
          ) : null
        }
        refreshing={loading}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        contentContainerStyle={filteredTournaments.length === 0 && styles.emptyList}
      />

      {/* Filters Modal */}
      <FiltersModal
        visible={filtersVisible}
        onClose={() => setFiltersVisible(false)}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterType={filterType}
        setFilterType={setFilterType}
        sortBy={sortBy}
        setSortBy={setSortBy}
        colors={colors}
      />

      {/* Actions Menu Modal */}
      <Modal visible={menuVisible} transparent animationType="fade" onRequestClose={() => setMenuVisible(false)}>
        <TouchableOpacity
          style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}
          activeOpacity={1}
          onPress={() => setMenuVisible(false)}
        >
          <View style={[styles.menuModal, { backgroundColor: colors.card }]}>
            <TouchableOpacity style={styles.menuItem} onPress={handleEdit}>
              <View style={styles.menuItemContent}>
                <FontAwesome5 name="edit" size={16} color={colors.text} />
                <Text style={[styles.menuItemText, { color: colors.text }]}>{t('common.edit')}</Text>
              </View>
            </TouchableOpacity>
            <View style={[styles.menuDivider, { backgroundColor: colors.border }]} />
            <TouchableOpacity style={styles.menuItem} onPress={handleDelete}>
              <View style={styles.menuItemContent}>
                <FontAwesome5 name="trash-alt" size={16} color={colors.error} />
                <Text style={[styles.menuItemText, { color: colors.error }]}>{t('common.delete')}</Text>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </PatternBackground>
  );
};
